import { useState } from "react";
import { Trash2, Save, Plus, AlertCircle } from "lucide-react";

export function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <span className="eyebrow text-slate-600 block mb-2">{label}</span>
      {children}
      {error ? (
        <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1.5">{hint}</p>
      ) : null}
    </label>
  );
}

export function TextInput({ value = "", onChange, ...rest }) {
  return (
    <input
      type="text"
      className="input-soft"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

export function NumberInput({ value = 0, onChange, ...rest }) {
  return (
    <input
      type="number"
      className="input-soft"
      value={value ?? 0}
      onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
      {...rest}
    />
  );
}

export function TextArea({ value = "", onChange, rows = 4, ...rest }) {
  return (
    <textarea
      rows={rows}
      className="input-soft resize-none"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

export function Toggle({ value, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-3 rounded-full border px-4 py-2 text-xs font-bold transition ${
        value
          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
          : "bg-slate-100 border-slate-200 text-slate-600"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          value ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {value ? "Active" : "Hidden"}
      {label && <span className="text-slate-500 font-medium">· {label}</span>}
    </button>
  );
}

export function StringListEditor({ value = [], onChange, placeholder = "Add item" }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="grid gap-2">
      <ul className="grid gap-2">
        {(value || []).map((item, i) => (
          <li
            key={i}
            className="flex items-center gap-2 rounded-2xl bg-white border border-slate-200 px-3 py-2"
          >
            <input
              className="flex-1 bg-transparent text-sm focus:outline-none"
              value={item}
              onChange={(e) => {
                const next = [...value];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              className="text-slate-400 hover:text-red-600"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <input
          className="input-soft flex-1"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (draft.trim()) {
                onChange([...(value || []), draft.trim()]);
                setDraft("");
              }
            }
          }}
        />
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2.5 text-xs font-bold"
          onClick={() => {
            if (draft.trim()) {
              onChange([...(value || []), draft.trim()]);
              setDraft("");
            }
          }}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function SaveBar({ onSave, saving, disabled, label = "Save changes" }) {
  return (
    <div className="sticky bottom-4 mt-8 flex justify-end">
      <button
        type="button"
        onClick={onSave}
        disabled={saving || disabled}
        className="btn-primary disabled:opacity-60"
        data-testid="admin-save-btn"
      >
        {saving ? "Saving…" : label}
        <Save className="h-4 w-4" />
      </button>
    </div>
  );
}

// =========================================================================
// v3.8 admin form primitives
// =========================================================================

/**
 * Tabbed form layout. Tabs are local to the form instance (state held inside).
 * Usage:
 *   <Tabs items={[{key:'basic',label:'Basic Info'}, …]}>
 *     {(activeKey) => activeKey === 'basic' ? <BasicForm /> : ...}
 *   </Tabs>
 */
export function Tabs({ items = [], initialKey, children, testId = "admin-form-tabs" }) {
  const [active, setActive] = useState(initialKey || items[0]?.key);
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3 mb-6">
        {items.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            data-testid={`${testId}-${t.key}`}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
              active === t.key
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid gap-5">{children(active)}</div>
    </div>
  );
}

export function Checkbox({ value, onChange, label, hint, ...rest }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300"
        {...rest}
      />
      <span>
        <span className="font-semibold">{label}</span>
        {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

export function Select({ value, onChange, options = [], ...rest }) {
  return (
    <select
      className="input-soft appearance-none"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    >
      {options.map((o) =>
        typeof o === "string" ? (
          <option key={o} value={o}>
            {o}
          </option>
        ) : (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        )
      )}
    </select>
  );
}

/**
 * Hex colour input with native colour picker + text fallback.
 * Empty string is valid (means "use fallback").
 */
export const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function ColorInput({ value = "", onChange, ...rest }) {
  const valid = !value || HEX_RE.test(value);
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value && HEX_RE.test(value) ? value : "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 rounded-lg border border-slate-200 cursor-pointer bg-white"
        aria-label="Colour picker"
      />
      <input
        type="text"
        className={`input-soft flex-1 font-mono text-sm ${
          !valid ? "border-red-300 ring-1 ring-red-200" : ""
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#4CAF50"
        {...rest}
      />
      {value && valid && (
        <span
          className="h-7 w-7 rounded-full border border-slate-200"
          style={{ backgroundColor: value }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

/**
 * JSON textarea with live parse + validation.
 * value: parsed object/array (or undefined). onChange: receives parsed value.
 * onValidityChange(isValid, errorMessage) — admin pages call this to gate save.
 */
export function JsonEditor({
  value,
  onChange,
  onValidityChange,
  rows = 8,
  placeholder = "[]",
  testId,
}) {
  const [text, setText] = useState(() =>
    value === undefined || value === null ? "" : JSON.stringify(value, null, 2)
  );
  const [error, setError] = useState("");

  const handle = (raw) => {
    setText(raw);
    if (raw.trim() === "") {
      setError("");
      onChange?.(undefined);
      onValidityChange?.(true, "");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setError("");
      onChange?.(parsed);
      onValidityChange?.(true, "");
    } catch (e) {
      const msg = e.message || "Invalid JSON";
      setError(msg);
      onValidityChange?.(false, msg);
    }
  };

  return (
    <div className="grid gap-2">
      <textarea
        rows={rows}
        className={`input-soft font-mono text-xs resize-y ${
          error ? "border-red-300 ring-1 ring-red-200" : ""
        }`}
        value={text}
        placeholder={placeholder}
        onChange={(e) => handle(e.target.value)}
        data-testid={testId}
      />
      {error && (
        <p className="text-[11px] text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Invalid JSON: {error}
        </p>
      )}
    </div>
  );
}

/**
 * Quick HEX colour-string validator (export for use in resource-page validators).
 * Returns true when value is empty OR matches #RGB / #RRGGBB.
 */
export function isValidHex(value) {
  return !value || HEX_RE.test(value);
}

/**
 * Basic URL validator — empty is allowed; if filled must start with http(s)://
 */
export function isValidUrl(value) {
  if (!value) return true;
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
